import React, { useState } from 'react';
import './SuggestedPromptsPage.scss';
import Title from '../../../shared/components/title/Title';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import useSuggestedQuestions from '../../../shared/hooks/suggested-prompts/UseSuggestedQuestions';
import Loader from '../../../shared/components/common/loader/Loader';
import { MonitorQuestion } from '../../../shared/models/monitor/MonitorQuestion';
import { PROJECTS } from '../../../shared/routes/path';
import { Navigate, useParams } from 'react-router-dom';
import { SuggestedPrompts } from '../../../shared/models/suggested-prompts/SuggestedQuestions';
import CustomTooltip from '../../../shared/components/common/tooltip/CustomTooltip';
import LimitTooltipComponent from '../../../shared/components/common/limit-tooltip/LimitTooltipComponent';
import useCanAddProject from '../../../shared/hooks/account/UseAddProject';
import { usePutMonitorsForProject } from '../../../shared/api/hooks/monitor/usePutMonitorsForProject';
import useIsMobile from '../../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../../shared/components/common/mobile-menu/MobilePageHeader';
import useToast from '../../../shared/hooks/global/UseToast';
import EmptyState from '../../../shared/components/common/empty-state/EmptyState';

const SuggestedPromptsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse, projectResponse, refetchAccountSilently } = useAccount();
  const { projectId } = useParams();
  const projectIdNumber = Number(projectId);

  const [submittingSlug, setSubmittingSlug] = useState<string | null>(null);

  const { loading, suggestedPromptsResponse, replaceQuestion, leavingSlug, enteringSlug } =
    useSuggestedQuestions(projectIdNumber);
  const { canAddProject } = useCanAddProject();
  const { addMonitorPrompts } = usePutMonitorsForProject(projectIdNumber);
  const { showToast, toastElement } = useToast();

  if (!accountResponse || !accountResponse.addons.includes('SUGGESTED_PROMPTS')) {
    return <Navigate to={PROJECTS} replace />;
  }

  const addQuestion = async (slug: string) => {
    if (!canAddProject) return;

    setSubmittingSlug(slug);

    if (!suggestedPromptsResponse || !projectIdNumber) {
      setSubmittingSlug(null);
      return;
    }

    const question = suggestedPromptsResponse.suggestedPrompts.find((q) => q.slug === slug);
    if (!question) {
      setSubmittingSlug(null);
      return;
    }

    const data = {
      prompt: question.question,
      status: 'PENDING',
    } as MonitorQuestion;

    try {
      await addMonitorPrompts({
        monitorQuestions: [data],
        projectId: projectIdNumber,
      });

      showToast(t('suggestedPromptAdded'));
      // Silent: refetchData would null the account and remount the layout behind a loader. The row keeps
      // its spinner until the monitor count (limit) and the next suggestion are both in.
      await Promise.all([refetchAccountSilently(), replaceQuestion(slug)]);
    } catch (error) {
      console.error('Error adding question:', error);
    } finally {
      setSubmittingSlug(null);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const searchVolumeBodyTemplate = (data: SuggestedPrompts) => {
    const isMoreThan100 = data.searchVolume > 100;
    return (
      <div className="search-volume">
        <div
          className={`search-volume__value search-volume__value${isMoreThan100 ? '--more' : '--less'}`}
        >
          {isMoreThan100 ? Intl.NumberFormat('pl-PL').format(data.searchVolume) : '<100'}
        </div>
        <div className="additional-panel">{data.phrase}</div>
      </div>
    );
  };

  const actionBodyTemplate = (slug: string, isAdded: boolean) => {
    const project = projectResponse?.find((project) => project.id === projectIdNumber);
    const isDemo = projectResponse && project?.demoVersion !== null && project?.demoVersion !== '';
    const isSubmitting = submittingSlug === slug;
    const isAnySubmitting = submittingSlug !== null;
    const isDisabled = isAnySubmitting || isAdded || !!isDemo || !canAddProject;

    return (
      <>
        {/* The spinner goes INSIDE the button: swapping the button for the smaller loader shrank the row. */}
        <Button
          id={`btn-add-question-${slug}`}
          className={`action-btn ${!canAddProject ? 'btn--disabled-tooltip' : ''}`}
          onClick={(e) => {
            // The button turns disabled and its row is swapped out, so the real mouseleave never comes —
            // without this the "Add to project" tooltip stays stuck on screen.
            e.currentTarget.dispatchEvent(new MouseEvent('mouseleave'));
            addQuestion(slug);
          }}
          disabled={isDisabled}
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <span className="table__cell-loader" />
          ) : (
            <span className={`ico--add ico--xs ${isDisabled && 'ico--disabled'}`} />
          )}
        </Button>
        {!canAddProject ? (
          <LimitTooltipComponent id={`btn-add-question-${slug}`} direction="top" />
        ) : (
          <CustomTooltip tooltipText={t('addToProject')} targetRef={`btn-add-question-${slug}`} />
        )}
      </>
    );
  };

  return (
    <>
      <ConfirmDialog className="dialog-box" />
      {toastElement}
      <div className="suggested-prompt-page page-content">
        <div
          className={`page-box${suggestedPromptsResponse?.suggestedPrompts?.length ? '' : ' suggested-prompt-page__box--empty'}`}
        >
          {/* Telefon: tytuł w górnym pasku zamiast nagłówka strony. */}
          <MobilePageHeader title={<span>{t('suggestedPrompts')}</span>} />
          {!isMobile && (
            <Title
              title={t('suggestedPrompts')}
              childrenOnMain={<div className="title-box__subtitle">{t('discoverNewPrompts')}</div>}
            />
          )}
          {/* Pula pusta: worker dogeneruje pytania w tle (suggestedQuestionsRefill). Układ jak na Tags. */}
          {!suggestedPromptsResponse?.suggestedPrompts?.length ? (
            <EmptyState emptyStateData={{ title: t('suggestedPromptsGeneratingSoon'), imageClassName: 'img--project' }} />
          ) : (
          <div className="page-box__stats">
                <div className={`table-content`}>
                  <DataTable
                    stripedRows
                    responsiveLayout="stack"
                    removableSort
                    value={suggestedPromptsResponse.suggestedPrompts}
                    tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
                    tableClassName="table"
                    className="table-wrapper table-wrapper--stack"
                    rowClassName={(row: SuggestedPrompts) => ({
                      'suggested-prompt-page__row--leaving': row.slug === leavingSlug,
                      'suggested-prompt-page__row--entering': row.slug === enteringSlug,
                    })}
                  >
                    <Column
                      sortable
                      field="prompt"
                      header={t('question')}
                      body={(data) => <div className="row-cell">{data.question || ''}</div>}
                      headerClassName="table__header"
                      bodyClassName="table__cell--stack-title"
                      style={{ minWidth: '120px', maxWidth: '100%' }}
                    />

                    <Column
                      sortable
                      field={'searchVolume'}
                      header={t('searchVolume')}
                      body={(data) => <div className="row-cell">{searchVolumeBodyTemplate(data)}</div>}
                      headerClassName="table__header table__header--center"
                      bodyClassName="table__cell--center"
                      style={{ width: '160px' }}
                    />
                    <Column
                      header={t('addToProject')}
                      body={(data) => (
                        <div className="row-cell">{actionBodyTemplate(data.slug, data.isAdded)}</div>
                      )}
                      bodyClassName="table__cell--gap table__cell--center table__cell--stack-actions"
                      headerClassName="table__header table__header--center"
                      align={'center'}
                      style={{ width: '34px' }}
                    />
                  </DataTable>
                </div>
          </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SuggestedPromptsPage;
